import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../components/Breadcrumbs';
import { stadtteile } from '../data/stadtteile';
import { seoData } from '../data/seoData';
import SchemaOrg from '../components/SchemaOrg';
import GoogleReviews from '../components/GoogleReviews';

const StadtteilPage = () => {
  // Extract slug from URL path since we're using explicit routes
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  const fullSlug = pathSegments[pathSegments.length - 1];
  // Remove 'schluesseldienst-dortmund-' prefix
  const slug = fullSlug.replace('schluesseldienst-dortmund-', '');
  
  const stadtteil = stadtteile.find(st => st.slug === slug);
  
  if (!stadtteil) {
    return <div>Stadtteil nicht gefunden</div>;
  }
  
  const seoKey = `/schluesseldienst-dortmund-${slug}/`;
  const seo = seoData[seoKey] || { 
    title: `Schlüsseldienst ${stadtteil.name}, Dortmund`, 
    description: `Schnelle Türöffnung in ${stadtteil.name}` 
  };
  
  const breadcrumbItems = [
    { name: 'Start', url: '/schluesseldienst-dortmund' },
    { name: 'Einsatzgebiete', url: '/einsatzgebiete' },
    { name: stadtteil.name, url: `/schluesseldienst-dortmund-${slug}` }
  ];
  
  const phone = '+4923139085116';
  const displayPhone = '0231 39085116';
  
  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={`https://schluesseldienst.services/schluesseldienst-dortmund-${slug}/`} />
      </Helmet>
      <SchemaOrg type="service" data={{ breadcrumbItems }} />
      
      <div className="container mx-auto px-4 py-12">
        <Breadcrumbs items={[
          { label: 'Einsatzgebiete', link: '/einsatzgebiete/' },
          { label: stadtteil.name }
        ]} />
        
        <h1 className="text-4xl sm:text-5xl font-bold mb-8">Schlüsseldienst {stadtteil.name}, Dortmund</h1>
        
        <div className="max-w-4xl">
          {/* Hero Card */}
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl shadow-lg p-8 mb-12 border-2 border-orange-200">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">Schnelle Hilfe in {stadtteil.name}</h2>
                <p className="text-lg">PLZ: {stadtteil.plz.join(', ')}</p>
              </div>
              <div>
                <a
                  href={`tel:${phone}`}
                  className="inline-block bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full font-bold transition-all shadow-lg"
                  data-testid="stadtteil-call-button"
                >
                  {displayPhone}
                </a>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="prose max-w-none mb-12">
            <h2>Ihr zuverlässiger Schlüsseldienst in {stadtteil.name}</h2>
            <p>
              Ausgesperrt in {stadtteil.name}? Unser Schlüsseldienst ist schnell bei Ihnen! 
              Wir kennen {stadtteil.name} und die Verkehrsanbindungen bestens, sodass wir in der Regel 
              innerhalb von 15-30 Minuten bei Ihnen sind. Als lokales Unternehmen mit Sitz in Dortmund 
              garantieren wir Ihnen faire Festpreise ohne versteckte Kosten.
            </p>

            <h3>Unsere Leistungen in {stadtteil.name}</h3>
            <p>
              Unser erfahrenes Team bietet Ihnen professionelle Hilfe bei allen Problemen rund um 
              Schlösser und Sicherheitstechnik in {stadtteil.name}:
            </p>
            <ul>
              <li><strong>Türöffnung bei zugefallener Tür:</strong> Die häufigste Notfallsituation. Wir öffnen Ihre Tür schonend und ohne Beschädigung.</li>
              <li><strong>Öffnung verschlossener Türen:</strong> Auch bei abgeschlossenen Türen finden wir eine Lösung.</li>
              <li><strong>Schlosswechsel und Zylinderaustausch:</strong> Nach einem Einbruch oder Schlüsselverlust tauschen wir Ihr Schloss fachgerecht aus.</li>
              <li><strong>Beratung zum Einbruchschutz:</strong> Wir zeigen Ihnen, wie Sie Ihr Zuhause in {stadtteil.name} optimal schützen können.</li>
              <li><strong>Tresoröffnung:</strong> Professionelle Öffnung ohne Beschädigung des Inhalts.</li>
              <li><strong>Autoöffnung:</strong> Auch bei verschlossenen Fahrzeugen helfen wir schnell und zuverlässig.</li>
            </ul>

            <h3>Transparente Festpreise für {stadtteil.name}</h3>
            <p>
              Wir glauben an ehrliche und transparente Preisgestaltung. Deshalb nennen wir Ihnen bereits 
              am Telefon unsere Festpreise:
            </p>
            <p>
              <strong>Mo–Fr 08–20 Uhr: 119 €</strong> (inkl. Anfahrt und MwSt.)<br/>
              <strong>Sa–So: 199 €</strong> (inkl. Anfahrt und MwSt.)
            </p>
            <p>
              Die Anfahrt nach {stadtteil.name} ist bereits im Preis enthalten. Wir akzeptieren 
              Barzahlung, EC-Karte und alle gängigen Kreditkarten. Keine Nachtzuschläge, keine versteckten Gebühren – 
              nur faire und nachvollziehbare Preise.
            </p>

            <h3>Warum wir in {stadtteil.name} die richtige Wahl sind</h3>
            <p>
              Als etablierter Schlüsseldienst in Dortmund kennen wir {stadtteil.name} genau. Unsere 
              Techniker sind mit den lokalen Gegebenheiten vertraut und finden immer den schnellsten Weg 
              zu Ihnen. Mit modernster Ausrüstung und jahrelanger Erfahrung öffnen wir über 95% aller 
              Türen ohne Beschädigung.
            </p>

            <h3>Typische Einsätze in {stadtteil.name}</h3>
            <p>
              In {stadtteil.name} werden wir häufig gerufen, wenn Bewohner sich ausgesperrt haben oder 
              der Schlüssel abgebrochen ist. Auch nach Einbrüchen oder bei defekten Schlössern sind wir 
              schnell zur Stelle. Dank unserer schonenden Öffnungstechnik können wir in den meisten Fällen 
              die Tür öffnen, ohne das Schloss zu beschädigen. Das spart Ihnen Zeit und zusätzliche Kosten.
            </p>

            <h3>So erreichen Sie uns für {stadtteil.name}</h3>
            <p>
              Rufen Sie uns einfach unter <strong>0231 39085116</strong> an. Wir sind 24 Stunden am Tag, 
              7 Tage die Woche für Sie erreichbar. Am Telefon schildern Sie uns kurz Ihre Situation und 
              erhalten sofort einen verbindlichen Festpreis. Unser Techniker macht sich dann sofort auf 
              den Weg nach {stadtteil.name}.
            </p>

            <h3>Häufige Fragen von Kunden aus {stadtteil.name}</h3>
            <div className="bg-gray-50 p-6 rounded-lg space-y-4">
              <div>
                <h4 className="font-bold mb-2">Wie schnell sind Sie in {stadtteil.name} vor Ort?</h4>
                <p className="mb-0">
                  In der Regel erreichen wir Sie in {stadtteil.name} innerhalb von 15-30 Minuten, 
                  abhängig von der Verkehrslage und unserem aktuellen Standort. Bei Notfällen priorisieren 
                  wir Ihre Anfrage entsprechend.
                </p>
              </div>
              
              <div>
                <h4 className="font-bold mb-2">Wird die Tür beschädigt?</h4>
                <p className="mb-0">
                  Nein, wir verwenden professionelle Werkzeuge und Techniken für eine schonende Öffnung. 
                  In über 95% der Fälle öffnen wir ohne Beschädigung von Tür oder Schloss.
                </p>
              </div>
              
              <div>
                <h4 className="font-bold mb-2">Welche Zahlungsarten sind möglich?</h4>
                <p className="mb-0">
                  Wir akzeptieren Barzahlung, EC-Karte und alle gängigen Kreditkarten direkt vor Ort in {stadtteil.name}.
                </p>
              </div>

              <div>
                <h4 className="font-bold mb-2">Sind die Preise wirklich fix?</h4>
                <p className="mb-0">
                  Ja, die am Telefon genannten Preise sind verbindlich. Es gibt keine versteckten Kosten oder 
                  Überraschungen. Sie zahlen genau den Betrag, den wir Ihnen vorab genannt haben.
                </p>
              </div>
            </div>
          </div>

          {/* Preistabelle */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-12">
            <div className="bg-orange-600 text-white p-4">
              <h2 className="text-2xl font-bold">Preise für {stadtteil.name}</h2>
            </div>
            <table className="w-full">
              <tbody>
                <tr className="border-b">
                  <td className="p-4">Mo–Fr 08–20 Uhr</td>
                  <td className="p-4 text-right text-2xl font-bold text-orange-600">119 €</td>
                </tr>
                <tr>
                  <td className="p-4">Sa–So</td>
                  <td className="p-4 text-right text-2xl font-bold text-orange-600">199 €</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Related Links */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-bold mb-4">Weitere Informationen</h2>
            <div className="space-y-2">
              <Link to="/preise" className="block text-orange-600 hover:underline">→ Alle Preise ansehen</Link>
              <Link to="/einsatzgebiete" className="block text-orange-600 hover:underline">→ Andere Stadtteile</Link>
              <Link to="/ratgeber" className="block text-orange-600 hover:underline">→ Ratgeber</Link>
            </div>
          </div>

          {/* Google Review CTA */}
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-200">
            <div className="text-center sm:text-left">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Zufrieden mit unserem Service?
              </h3>
              <p className="text-gray-700 mb-4 sm:mb-0">
                Teilen Sie Ihre Erfahrung und helfen Sie anderen Kunden in {stadtteil.name}!
              </p>
            </div>
            <div className="flex-shrink-0">
              <GoogleReviews variant="cta" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StadtteilPage;
